import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "@/components/ui/dialog";
import { TierBadge, StatusPill, ScoreBar, STATUS_LABELS, TIER_META } from "@/components/Bits";
import { ArrowLeft, Sparkles, Send, RefreshCw, Trash2, CheckCircle2, ExternalLink } from "lucide-react";

const CATEGORIES = ["hospitality", "events", "retail", "services", "charity", "community", "other"];
const SIZES = ["small", "medium", "large"];

function EnrollDialog({ leadId, onEnrolled }) {
  const [open, setOpen] = useState(false);
  const [campaigns, setCampaigns] = useState([]);
  const [pick, setPick] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    api.get("/campaigns").then((r) => setCampaigns(r.data.items));
  }, [open]);
  const submit = async () => {
    if (!pick) return;
    setBusy(true);
    try {
      const r = await api.post("/campaigns/enroll", { campaign_id: pick, lead_ids: [leadId] });
      toast.success(`Enrolled (${r.data.enrolled}), skipped (${r.data.skipped})`);
      setOpen(false);
      onEnrolled && onEnrolled();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Enroll failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="bg-[#1B4332] hover:bg-[#2D5A47] rounded-sm text-white" data-testid="enroll-button">
          <Send className="h-4 w-4 mr-1.5" /> Enroll in Campaign
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="enroll-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">Enroll lead in campaign</DialogTitle></DialogHeader>
        <Select value={pick} onValueChange={setPick}>
          <SelectTrigger data-testid="enroll-select-campaign"><SelectValue placeholder="Choose a campaign" /></SelectTrigger>
          <SelectContent>
            {campaigns.map((c) => (
              <SelectItem key={c.campaign_id} value={c.campaign_id}>{c.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <DialogFooter>
          <Button variant="outline" onClick={() => setOpen(false)} className="rounded-sm">Cancel</Button>
          <Button onClick={submit} disabled={!pick || busy} className="bg-[#1B4332] hover:bg-[#2D5A47] rounded-sm" data-testid="submit-enroll">
            {busy ? "Enrolling…" : "Enroll"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function LeadDetail() {
  const { leadId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [rescoring, setRescoring] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/leads/${leadId}`);
      setData(res.data);
    } catch (e) {
      toast.error("Lead not found");
      navigate("/leads");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [leadId]);

  const save = async (patch) => {
    setSaving(true);
    try {
      const res = await api.patch(`/leads/${leadId}`, patch);
      setData({ ...data, lead: res.data });
      toast.success("Saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const rescore = async (useAi) => {
    setRescoring(true);
    try {
      const res = await api.post(`/leads/${leadId}/score?use_ai=${useAi ? "true" : "false"}`);
      setData({ ...data, lead: res.data.lead });
      toast.success(useAi ? "AI re-scored" : "Rules-based re-scored");
    } catch (e) {
      toast.error("Score failed");
    } finally {
      setRescoring(false);
    }
  };

  const del = async () => {
    if (!window.confirm("Delete this lead?")) return;
    try {
      await api.delete(`/leads/${leadId}`);
      toast.success("Lead deleted");
      navigate("/leads");
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  if (loading || !data) return <div className="text-sm text-gray-500">Loading…</div>;
  const lead = data.lead;

  return (
    <div data-testid="lead-detail-page">
      <Link to="/leads" className="inline-flex items-center text-xs text-gray-500 hover:text-[#1B4332] mb-4" data-testid="back-to-leads">
        <ArrowLeft className="h-3 w-3 mr-1" /> Back to leads
      </Link>

      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="font-heading text-4xl tracking-tight">{lead.business_name}</h1>
            <TierBadge tier={lead.suggested_tier} size="lg" />
            <StatusPill status={lead.status} />
          </div>
          <div className="text-sm text-gray-600 mt-2 flex flex-wrap items-center gap-x-4 gap-y-1">
            {lead.category && <span className="capitalize">{lead.category}</span>}
            {lead.location && <span>· {lead.location}</span>}
            {lead.business_size && <span>· <span className="capitalize">{lead.business_size}</span></span>}
            {lead.website && (
              <a href={lead.website} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-[#1B4332] hover:underline">
                {lead.website} <ExternalLink className="h-3 w-3" />
              </a>
            )}
          </div>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button onClick={() => rescore(false)} variant="outline" className="rounded-sm" disabled={rescoring} data-testid="rescore-rules-button">
            <RefreshCw className="h-4 w-4 mr-1.5" /> Rules re-score
          </Button>
          <Button onClick={() => rescore(true)} className="bg-[#101C26] hover:bg-black text-white rounded-sm" disabled={rescoring} data-testid="rescore-ai-button">
            <Sparkles className="h-4 w-4 mr-1.5" /> AI score
          </Button>
          <EnrollDialog leadId={leadId} onEnrolled={load} />
          <Button variant="outline" onClick={del} className="rounded-sm text-red-700 border-red-300 hover:bg-red-50" data-testid="delete-lead-button">
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left: fields */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="lead-fields-card">
            <div className="uppercase-tiny text-gray-500 mb-4">Details</div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Business name</Label>
                <Input defaultValue={lead.business_name} onBlur={(e) => e.target.value !== lead.business_name && save({ business_name: e.target.value })} data-testid="field-business-name" />
              </div>
              <div>
                <Label>Website</Label>
                <Input defaultValue={lead.website || ""} onBlur={(e) => e.target.value !== (lead.website || "") && save({ website: e.target.value })} data-testid="field-website" />
              </div>
              <div>
                <Label>Contact name</Label>
                <Input defaultValue={lead.contact_name || ""} onBlur={(e) => save({ contact_name: e.target.value })} data-testid="field-contact-name" />
              </div>
              <div>
                <Label>Contact email</Label>
                <Input type="email" defaultValue={lead.contact_email || ""} onBlur={(e) => save({ contact_email: e.target.value })} data-testid="field-contact-email" />
              </div>
              <div>
                <Label>Phone</Label>
                <Input defaultValue={lead.phone || ""} onBlur={(e) => save({ phone: e.target.value })} data-testid="field-phone" />
              </div>
              <div>
                <Label>Location</Label>
                <Input defaultValue={lead.location || ""} onBlur={(e) => save({ location: e.target.value })} data-testid="field-location" />
              </div>
              <div>
                <Label>Category</Label>
                <Select defaultValue={lead.category || undefined} onValueChange={(v) => save({ category: v })}>
                  <SelectTrigger data-testid="field-category"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label>Business size</Label>
                <Select defaultValue={lead.business_size || undefined} onValueChange={(v) => save({ business_size: v })}>
                  <SelectTrigger data-testid="field-size"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent>{SIZES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <div className="mt-4">
              <Label>Notes</Label>
              <Textarea rows={4} defaultValue={lead.notes || ""} onBlur={(e) => save({ notes: e.target.value })} data-testid="field-notes" />
            </div>
          </div>

          {/* Activity */}
          <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="lead-activity-card">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="uppercase-tiny text-gray-500">Outreach activity</div>
                <div className="font-heading text-xl mt-1">Timeline</div>
              </div>
            </div>
            {data.events.length === 0 && (
              <div className="text-sm text-gray-500 py-6 text-center border border-dashed border-gray-200 rounded-sm">
                No outreach yet.
              </div>
            )}
            <ol className="relative border-l border-gray-200 pl-4 space-y-4">
              {data.events.map((ev) => (
                <li key={ev.event_id} className="relative" data-testid={`event-${ev.event_id}`}>
                  <span className="absolute -left-6 top-1 h-2.5 w-2.5 rounded-full bg-[#1B4332]" />
                  <div className="text-xs text-gray-500">{ev.created_at?.replace("T", " ").slice(0, 16)}</div>
                  <div className="text-sm">
                    <span className="uppercase text-[10px] font-bold tracking-wider text-gray-700 mr-2">{ev.event_type}</span>
                    {ev.subject && <span className="text-gray-800">{ev.subject}</span>}
                  </div>
                  {ev.error && <div className="text-xs text-red-600 mt-1">{ev.error}</div>}
                </li>
              ))}
            </ol>
          </div>
        </div>

        {/* Right: fit + status */}
        <div className="space-y-6">
          <div className={`bg-white border border-gray-200 rounded-sm p-6 tier-border-${lead.suggested_tier || "silver"}`} data-testid="lead-fit-card">
            <div className="uppercase-tiny text-gray-500 mb-2">Fit score</div>
            <div className="flex items-baseline gap-2">
              <div className="font-heading text-5xl">{lead.fit_score ?? "—"}</div>
              <div className="text-sm text-gray-500">/ 100</div>
            </div>
            <div className="mt-3"><ScoreBar score={lead.fit_score ?? 0} /></div>
            {lead.suggested_tier && (
              <div className="mt-4">
                <div className="uppercase-tiny text-gray-500 mb-1">Recommended tier</div>
                <div className="flex items-center gap-3">
                  <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: TIER_META[lead.suggested_tier]?.color }} />
                  <div>
                    <div className="font-heading text-lg">{TIER_META[lead.suggested_tier]?.name} Partner</div>
                    <div className="text-xs text-gray-500">£{TIER_META[lead.suggested_tier]?.price} / year</div>
                  </div>
                </div>
              </div>
            )}
            {lead.fit_reasoning && (
              <div className="mt-4 text-xs text-gray-600 leading-relaxed border-t border-gray-100 pt-3" data-testid="fit-reasoning">
                {lead.fit_reasoning}
              </div>
            )}
          </div>

          <div className="bg-white border border-gray-200 rounded-sm p-6" data-testid="lead-status-card">
            <div className="uppercase-tiny text-gray-500 mb-3">Pipeline status</div>
            <Select value={lead.status} onValueChange={(v) => save({ status: v })}>
              <SelectTrigger data-testid="field-status"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATUS_LABELS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>

            <div className="mt-4">
              <label className="flex items-center gap-2 text-sm cursor-pointer">
                <input
                  type="checkbox"
                  checked={!!lead.approved_for_outreach}
                  onChange={(e) => save({ approved_for_outreach: e.target.checked })}
                  data-testid="field-approved"
                />
                <span>Approved for outreach</span>
              </label>
            </div>

            {lead.status === "converted" && (
              <div className="mt-4 space-y-3 border-t border-gray-100 pt-4">
                <div>
                  <Label>Converted tier</Label>
                  <Select defaultValue={lead.converted_tier || undefined} onValueChange={(v) => save({ converted_tier: v })}>
                    <SelectTrigger data-testid="field-converted-tier"><SelectValue placeholder="—" /></SelectTrigger>
                    <SelectContent>
                      {Object.entries(TIER_META).map(([k, v]) => (
                        <SelectItem key={k} value={k}>{v.name} — £{v.price}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Deal value (£)</Label>
                  <Input type="number" defaultValue={lead.deal_value || ""} onBlur={(e) => save({ deal_value: parseFloat(e.target.value) || 0 })} data-testid="field-deal-value" />
                </div>
                <div className="text-xs text-emerald-700 flex items-center gap-1.5">
                  <CheckCircle2 className="h-3.5 w-3.5" /> Converted deal recorded
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
